/**
 * Account Settings Page
 * Modern social media style design
 */

import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { supabase } from '../../services/supabase'
import { uploadSingleImage } from '../../services/uploadService'
import { toast } from '../../stores/toastStore'

// User details type
interface UserDetail {
  id: string
  email: string
  username: string
  avatar_url: string | null
  bio: string | null
}

// Social account type
interface SocialAccount {
  id: string
  platform: string
  platform_username: string
  is_active: boolean
}

const Settings: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user, openLoginModal } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // State management
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [name, setName] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string>('')

  // Social accounts state
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [isConnectingX, setIsConnectingX] = useState(false)
  const [isDisconnectingX, setIsDisconnectingX] = useState(false)

  // Fetch user details
  useEffect(() => {
    const fetchUserDetail = async () => {
      if (!user) return

      try {
        setIsLoading(true)
        const { data, error: fetchError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single()

        if (fetchError) throw fetchError

        setUserDetail(data)
        setName(data.username || '')
        setBio(data.bio || '')
        setAvatarUrl(data.avatar_url)
      } catch (err) {
        console.error('Failed to fetch user info:', err)
        toast.error('Failed to fetch user info')
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserDetail()
  }, [user])

  // Fetch social accounts
  useEffect(() => {
    const fetchSocialAccounts = async () => {
      if (!user) return

      try {
        setIsLoadingAccounts(true)
        const { data, error: fetchError } = await supabase
          .from('user_social_accounts')
          .select('id, platform, platform_username, is_active')
          .eq('user_id', user.id)

        if (fetchError) throw fetchError

        setSocialAccounts(data || [])
      } catch (err) {
        console.error('Failed to fetch social accounts:', err)
      } finally {
        setIsLoadingAccounts(false)
      }
    }

    fetchSocialAccounts()
  }, [user])

  // Handle OAuth callback
  useEffect(() => {
    const platform = searchParams.get('platform')
    const status = searchParams.get('status')
    const username = searchParams.get('username')
    const errorCode = searchParams.get('code')
    const errorMessage = searchParams.get('message')

    if (platform === 'x') {
      if (status === 'success' && username) {
        toast.success(`X account @${username} connected successfully!`)
        // Refresh social accounts
        fetchSocialAccounts()
      } else if (status === 'error') {
        toast.error(`Failed to connect X account: ${errorMessage || errorCode || 'Unknown error'}`)
      }
      // Clear URL params
      window.history.replaceState({}, '', '/settings')
    }
  }, [searchParams])

  // Helper function to fetch social accounts
  const fetchSocialAccounts = async () => {
    if (!user) return

    try {
      const { data, error: fetchError } = await supabase
        .from('user_social_accounts')
        .select('id, platform, platform_username, is_active')
        .eq('user_id', user.id)

      if (fetchError) throw fetchError

      setSocialAccounts(data || [])
    } catch (err) {
      console.error('Failed to fetch social accounts:', err)
    }
  }

  // Handle avatar upload click
  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  // Handle avatar file selection
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size cannot exceed 10MB')
      return
    }

    setIsUploadingAvatar(true)
    setUploadProgress('Uploading avatar...')

    try {
      // 上传图片到 R2
      const uploadedUrl = await uploadSingleImage(file, user.id)

      if (!uploadedUrl) {
        throw new Error('Upload failed')
      }

      // Update avatar URL in database
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: uploadedUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id)

      if (updateError) throw updateError

      // Update local state
      setAvatarUrl(uploadedUrl)
      setUploadProgress('')
      toast.success('Avatar updated successfully')

      // 更新 auth metadata 中的头像
      await supabase.auth.updateUser({
        data: { avatar_url: uploadedUrl }
      })

      // Trigger Profile page data refresh
      queryClient.invalidateQueries({ queryKey: ['profileData', user.id] })
      queryClient.invalidateQueries({ queryKey: ['sidebarUserInfo', user.id] })
    } catch (err) {
      console.error('Avatar upload failed:', err)
      toast.error(err instanceof Error ? err.message : 'Avatar upload failed')
      setUploadProgress('')
    } finally {
      setIsUploadingAvatar(false)
      // 重置 input
      e.target.value = ''
    }
  }

  // Save changes
  const handleSave = async () => {
    if (!user || !userDetail) return

    // Validate name
    if (!name.trim()) {
      toast.error('Name cannot be empty')
      return
    }

    setIsSaving(true)

    try {
      // Update user info
      const { error: updateError } = await supabase
        .from('users')
        .update({
          username: name.trim(),
          bio: bio.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)

      if (updateError) {
        // Handle unique constraint error
        if (updateError.code === '23505') {
          throw new Error('This name is already taken')
        }
        throw updateError
      }

      // 更新 auth metadata
      await supabase.auth.updateUser({
        data: { full_name: name.trim() }
      })

      // Trigger Profile page data refresh
      queryClient.invalidateQueries({ queryKey: ['profileData', user.id] })
      queryClient.invalidateQueries({ queryKey: ['sidebarUserInfo', user.id] })

      toast.success('Saved successfully')
    } catch (err) {
      console.error('Save failed:', err)
      toast.error(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle sign out
  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/')
    openLoginModal()
  }

  // Get X account status
  const getXAccount = (): SocialAccount | undefined => {
    return socialAccounts.find(acc => acc.platform === 'x')
  }

  const xConnected = getXAccount()?.is_active ?? false
  const xUsername = getXAccount()?.platform_username ?? ''

  // Handle X account connect/disconnect
  const handleXAccount = async () => {
    if (!user) return

    const xAccount = getXAccount()

    if (xAccount && xAccount.is_active) {
      // Disconnect: delete the account record
      try {
        setIsDisconnectingX(true)

        const { error: deleteError } = await supabase
          .from('user_social_accounts')
          .delete()
          .eq('id', xAccount.id)

        if (deleteError) throw deleteError

        setSocialAccounts(prev => prev.filter(acc => acc.platform !== 'x'))
        toast.success('X account disconnected successfully')
      } catch (err) {
        console.error('Failed to disconnect X account:', err)
        toast.error('Failed to disconnect X account')
      } finally {
        setIsDisconnectingX(false)
      }
    } else {
      // Connect: call auth-x-connect to get OAuth URL
      try {
        setIsConnectingX(true)

        const { data: sessionData } = await supabase.auth.getSession()
        const accessToken = sessionData.session?.access_token

        if (!accessToken) {
          throw new Error('No authentication session found')
        }

        // Call auth-x-connect Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-x-connect`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ userId: user.id, redirect_to: '/settings' }),
          }
        )

        const result = await response.json()

        if (!result.success || !result.auth_url) {
          throw new Error(result.error || 'Failed to get authorization URL')
        }

        // Redirect to X OAuth authorization page
        window.location.href = result.auth_url
      } catch (err) {
        console.error('Failed to connect X account:', err)
        toast.error('Failed to initiate X account connection')
        setIsConnectingX(false)
      }
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] min-h-screen">
        <header className="sticky top-0 z-[90] flex items-center justify-center px-10 h-[72px] bg-white border-b border-[#EBEBEB]">
          {/* Mobile back button */}
          <button
            onClick={() => navigate('/profile')}
            className="md:hidden absolute left-4 text-[16px] text-[#333333] font-medium flex items-center gap-1"
          >
            <svg className="w-[18px] h-[18px] fill-current" viewBox="0 0 24 24">
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            </svg>
            Back
          </button>
          <div className="text-[18px] font-semibold text-[#333333]">Account Settings</div>
        </header>
        <div className="flex justify-center items-center py-20 text-[#999999]">Loading...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] text-[#333333] min-h-screen">
      {/* Top title bar */}
      <header className="sticky top-0 z-[90] flex items-center justify-center px-10 h-[72px] bg-white border-b border-[#EBEBEB]">
        {/* Mobile back button */}
        <button
          onClick={() => navigate('/profile')}
          className="md:hidden absolute left-4 text-[16px] text-[#333333] font-medium flex items-center gap-1"
        >
          <svg className="w-[18px] h-[18px] fill-current" viewBox="0 0 24 24">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
          </svg>
          Back
        </button>
        <div className="text-[18px] font-semibold text-[#333333]">Account Settings</div>
      </header>

      {/* Main content area */}
      <div className="p-10 max-w-[800px] mx-auto w-full">
        {/* Basic profile card */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 mb-8">
          <h2 className="text-[18px] font-semibold mb-6 text-[#333333]">Basic Profile</h2>

          {/* Avatar settings area */}
          <div className="flex items-center gap-6 mb-8 pb-6 border-b border-[#EBEBEB]">
            {/* Large avatar */}
            <img
              src={avatarUrl || 'https://via.placeholder.com/80?text=Avatar'}
              alt="Avatar"
              className="w-[80px] h-[80px] rounded-full object-cover border border-[#EBEBEB] bg-[#F5F5F7]"
            />

            {/* Change avatar button and hint */}
            <div className="flex flex-col">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
              <button
                onClick={handleAvatarClick}
                disabled={isUploadingAvatar}
                className={`py-2 px-4 border border-[#EBEBEB] bg-transparent rounded-full text-[14px] font-medium cursor-pointer text-[#333333] transition-colors mb-2 ${
                  isUploadingAvatar ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F5F5F7] hover:border-[#CCCCCC]'
                }`}
              >
                {isUploadingAvatar ? 'Uploading...' : 'Change Avatar'}
              </button>
              <div className="text-[12px] text-[#999999]">Recommended: 400x400 px or higher</div>
            </div>
          </div>

          {/* Name input */}
          <div className="mb-6">
            <label className="block font-medium mb-2.5 text-[14px] text-[#333333]">Name</label>
            <input
              type="text"
              className="w-full py-[14px] px-4 border border-[#EBEBEB] bg-[#FAFAFA] rounded-xl text-[15px] outline-none transition-colors text-[#333333] focus:border-[#999999] focus:bg-white"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Bio input */}
          <div className="mb-6">
            <label className="block font-medium mb-2.5 text-[14px] text-[#333333]">Bio</label>
            <input
              type="text"
              className="w-full py-[14px] px-4 border border-[#EBEBEB] bg-[#FAFAFA] rounded-xl text-[15px] outline-none transition-colors text-[#333333] focus:border-[#999999] focus:bg-white"
              placeholder="Tell us about yourself"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Upload progress indicator */}
          {uploadProgress && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 text-[14px]">
              {uploadProgress}
            </div>
          )}

          {/* Save changes button */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full py-[14px] text-white border-none rounded-full text-[16px] font-semibold cursor-pointer transition-opacity mt-4 shadow-[0_4px_12px_rgba(255,36,66,0.2)] ${
              isSaving ? 'bg-[#FF244280] cursor-not-allowed' : 'bg-[#FF2442] hover:opacity-90 hover:shadow-[0_6px_16px_rgba(255,36,66,0.3)]'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Account & Security card */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 mb-8">
          <h2 className="text-[18px] font-semibold mb-6 text-[#333333]">Account & Security</h2>

          {/* Binding list */}
          <div className="flex flex-col gap-4">
            {/* Email info */}
            <div className="flex items-center justify-between py-4 px-5 border border-[#EBEBEB] rounded-xl bg-[#FAFAFA] transition-colors hover:bg-white hover:border-[#CCCCCC]">
              <div className="flex items-center gap-4">
                {/* Email icon */}
                <div className="w-10 h-10 rounded-[10px] bg-white shadow-[0_2px_4px_rgba(0,0,0,0.05)] flex items-center justify-center text-[20px]">
                  <svg className="w-5 h-5 fill-[#666]" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-[15px] font-semibold text-[#333333] mb-1">Email Binding</div>
                  <div className="text-[13px] text-[#999999]">{userDetail?.email || user?.email || 'Not bound'}</div>
                </div>
              </div>
              {/* No action button shown */}
            </div>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 mb-8">
          <h2 className="text-[18px] font-semibold mb-6 text-[#333333]">Connected Accounts</h2>

          {/* X account status */}
          <div className="flex items-center justify-between py-4 px-5 border border-[#EBEBEB] rounded-xl bg-[#FAFAFA] transition-colors hover:bg-white hover:border-[#CCCCCC]">
            <div className="flex items-center gap-4">
              {/* X icon */}
              <div className="w-10 h-10 rounded-[10px] bg-white shadow-[0_2px_4px_rgba(0,0,0,0.05)] flex items-center justify-center">
                <span className="text-[18px] font-bold text-[#333333]">X</span>
              </div>
              <div>
                <div className="text-[15px] font-semibold text-[#333333] mb-1">X (Twitter)</div>
                <div className="text-[13px] text-[#999999]">
                  {isLoadingAccounts ? 'Loading...' :
                    xConnected ? `Connected as @${xUsername}` : 'Not connected'}
                </div>
              </div>
            </div>
            {/* Connect/Disconnect button */}
            <button
              onClick={handleXAccount}
              disabled={isConnectingX || isDisconnectingX || isLoadingAccounts}
              className={`py-2 px-4 rounded-full text-[14px] font-medium cursor-pointer transition-all ${
                xConnected
                  ? 'border border-[#EBEBEB] bg-transparent text-[#333333] hover:bg-[#F5F5F7] hover:border-[#CCCCCC]'
                  : 'bg-[#333333] text-white hover:bg-[#444444]'
              } ${isConnectingX || isDisconnectingX || isLoadingAccounts ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isConnectingX ? 'Connecting...' :
                isDisconnectingX ? 'Disconnecting...' :
                  xConnected ? 'Disconnect' : 'Connect'}
            </button>
          </div>

          {/* Other platforms placeholder */}
          <div className="mt-4 text-[13px] text-[#999999]">
            More platforms coming soon: LinkedIn, Threads...
          </div>
        </div>

        {/* Sign out button */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 mb-8">
          <button
            onClick={handleSignOut}
            className="w-full py-[14px] text-[#FF2442] border border-[#FF2442] bg-transparent rounded-full text-[16px] font-semibold cursor-pointer transition-all hover:bg-[#FF2442] hover:text-white"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .bg-white.rounded-2xl.shadow-\\[0_2px_12px_rgba\\(0\\,0\\,0\\,0\\.03\\)\\] {
            padding: 20px !important;
            margin-bottom: 24px !important;
          }

          .flex.items-center.gap-6.mb-8.pb-6.border-b.border-\\[\\#EBEBEB\\] {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .p-10.max-w-\\[800px\\].mx-auto.w-full {
            padding: 16px 16px 40px !important;
          }

          header.sticky.top-0 {
            padding: 0 16px !important;
            height: 60px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default Settings